import { FC, useEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import teamDataStatic from '@/data/teamFormData.json';
import { AnimatedCard } from '@/components/animation/AnimatedCard';
import { FeedbackInput } from '@/components/inputs/Feedback';
import axios from 'axios';
import { FormModal } from '@/components/common/FormModal';
import { Button } from '@/components/buttons/Button';

export interface IFormData {
  teamFormName: string;
  teamFormSurname: string;
  teamFormPhone: string;
  teamFormEmail: string;
  attachment: FileList | null;
  honeyPot: string;
}

export const TeamForm: FC = (): JSX.Element => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
    setValue,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useForm<any>({
    criteriaMode: 'all',
    mode: 'onChange',
  });

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [isSendErr, setIsSendErr] = useState(false);
  const [language, setLanguage] = useState<'EN' | 'UA'>('UA');
  const { t } = useTranslation('common');
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);

  const thankText = t('modalFormThank');
  const errText = t('serverErr');
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);

  const router = useRouter();

  useEffect(() => {
    router.locale == 'en' ? setLanguage('EN') : setLanguage('UA');
    reset();
  }, [reset, router]);

  const closeForm = () => {
    setIsOpenModal(false);
  };

  const formDataValues = teamDataStatic[language];

  const onSubmit: SubmitHandler<IFormData> = async data => {
    if (data.honeyPot && data.honeyPot.trim() !== '') {
      console.warn('Honeypot triggered. Possible bot.');
      return;
    }

    try {
      let uploadedFilename: string | null = null;

      if (data.attachment?.[0]) {
        const fileFormData = new FormData();
        fileFormData.append('file', data.attachment[0]);

        const uploadResponse = await axios.post('/api/upload', fileFormData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        if (uploadResponse.status === 200 && uploadResponse.data.filename) {
          uploadedFilename = uploadResponse.data.filename;
        } else {
          throw new Error('Не вдалося завантажити файл');
        }
      }

      const formData = new FormData();
      formData.append('title', 'Форма зворотнього зв’язку');
      formData.append('name', data.teamFormName);
      formData.append('surname', data.teamFormSurname);
      formData.append('phone', data.teamFormPhone);
      formData.append('email', data.teamFormEmail);
      console.log(uploadedFilename, 'uploadedFilename');
      console.log(
        `https://www.microkhim.com.ua/api/files/${uploadedFilename}`,
        'uri',
      );
      if (uploadedFilename) {
        formData.append(
          'attachmentUrl',
          `https://www.microkhim.com.ua/api/files/${uploadedFilename}`,
        );
        /*    `http://localhost:3000/api/files/${uploadedFilename}`, */
      }

      // mail - hr@office.microkhim.com
      const responseSend = await axios.post(
        'https://formsubmit.co/ajax/b951d7b5b911d82bb4b80ea49c957558',
        formData,
      );

      if (responseSend.status === 200) {
        setIsSendErr(false);
        setIsOpenModal(true);
        reset();
        setSelectedFileName(null);
      }
    } catch (error) {
      setIsSendErr(true);
      console.error(error);
    }
  };

  useEffect(() => {
    register('attachment', { required: true });
  }, [register]);

  return (
    <AnimatedCard>
      <form
        className="team-form"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="on"
      >
        {/* honeypot */}
        <input
          type="text"
          {...register('honeyPot')}
          style={{ display: 'none' }}
          tabIndex={-1}
          autoComplete="off"
        />
        <p className="team-form__title">{formDataValues.title}</p>
        <div className="team-form__wrapper">
          <div className="team-form__inner-wrapper w-full">
            <div className="team-form__input-wrapper relative">
              <FeedbackInput
                required={true}
                type={'text'}
                name={'teamFormName'}
                register={register}
                placeholder={formDataValues.name}
                onChange={() => clearErrors('teamFormName')}
                locale={language}
                className={errors?.teamFormName?.message ? 'err' : ''}
              />{' '}
              {errors?.teamFormName?.message && (
                <p className=" text-error">
                  {errors.teamFormName.message.toString()}
                </p>
              )}
            </div>
            <div className="team-form__input-wrapper relative">
              <FeedbackInput
                required={true}
                type={'text'}
                name={'teamFormSurname'}
                register={register}
                placeholder={formDataValues.surname}
                onChange={() => clearErrors('teamFormSurname')}
                locale={language}
                className={errors?.teamFormSurname?.message ? 'err' : ''}
              />{' '}
              {errors?.teamFormSurname?.message && (
                <p className=" text-error">
                  {errors.teamFormSurname.message.toString()}
                </p>
              )}
            </div>
          </div>

          <div className="team-form__inner-wrapper w-full">
            <div className="team-form__input-wrapper relative">
              <FeedbackInput
                required={true}
                type={'email'}
                name={'teamFormEmail'}
                register={register}
                placeholder={formDataValues.email}
                onChange={() => clearErrors('teamFormEmail')}
                locale={language}
                className={errors?.teamFormEmail?.message ? 'err' : ''}
              />{' '}
              {errors?.teamFormEmail?.message && (
                <p className=" text-error">
                  {errors.teamFormEmail.message.toString()}
                </p>
              )}
            </div>
            <div className="team-form__input-wrapper relative">
              <FeedbackInput
                required={true}
                type={'tel'}
                name={'teamFormPhone'}
                register={register}
                placeholder={formDataValues.phone}
                onChange={() => clearErrors('teamFormPhone')}
                locale={language}
                className={errors?.teamFormPhone?.message ? 'err' : ''}
              />{' '}
              {errors?.teamFormPhone?.message && (
                <p className="text-error">
                  {errors.teamFormPhone.message.toString()}
                </p>
              )}
            </div>
          </div>
          <div className="team-form__file-wrapper">
            <label
              htmlFor="attachment"
              className="block w-full cursor-pointer text-center transition hover:bg-gray-200"
            >
              {selectedFileName ? (
                <span className="team-form__file-selectedFileName">
                  {selectedFileName}
                </span>
              ) : (
                <>
                  <span className="team-form__file-title">
                    {formDataValues.file_1}
                  </span>
                  <br />
                  <span className="team-form__file-pre-title">
                    {formDataValues.file_2}
                  </span>
                </>
              )}

              <input
                id="attachment"
                type="file"
                accept=".doc,.docx,.rtf,.pdf"
                ref={attachmentInputRef}
                name="attachment"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) {
                    const MAX_SIZE_MB = 10;
                    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
                      alert(
                        `Файл перевищує ${MAX_SIZE_MB}MB! Оберіть менший файл.`,
                      );
                      e.target.value = '';
                      setSelectedFileName(null);
                      setValue('attachment', null, { shouldValidate: true });
                      return;
                    }

                    setSelectedFileName(file.name);
                    clearErrors('attachment');
                    setValue('attachment', e.target.files, {
                      shouldValidate: true,
                    });
                  } else {
                    setSelectedFileName(null);
                    setValue('attachment', null, { shouldValidate: true });
                  }
                }}
                className="hidden"
              />
            </label>
            {errors?.attachment && (
              <p className="text-error">{formDataValues.file_err}</p>
            )}
          </div>
        </div>
        <div
          className="team-form__agreement"
          dangerouslySetInnerHTML={{ __html: formDataValues.agreement }}
        />
        {formDataValues?.submit && (
          <Button className="team-form__button w-full max-w-full">
            {formDataValues.submit.label}
          </Button>
        )}
        {isSendErr ? <p className="pt-0.5 text-error">{errText}</p> : null}
      </form>
      <FormModal closeModal={closeForm} isOpen={isOpenModal} text={thankText} />
    </AnimatedCard>
  );
};
